import { Card, Employee, Shop } from "@/types/v2";
import { ToastFieldKey } from "@/lib/automationShowToast";
import { getCardSection } from "@/lib/cards";
import { getContainerName } from "@/lib/getContainerName";
import { getEmployeeName } from "@/lib/employees";
import { firestoreToDateTime } from "@/lib/utils";

/** Sentinel strings the lookup helpers return on a miss — these must not
 * survive into a rendered toast, so they're mapped back to "" (which
 * `resolveToastFields` then filters out) instead of being shown as-is. */
const UNKNOWN_CONTAINER_SENTINEL = "Unknown Container";
const UNKNOWN_EMPLOYEE_SENTINEL = "Unknown";

/**
 * Resolves one toast field key against a card.
 * @param {Card} card - The card.
 * @param {Shop} shop - The shop (for card-content config).
 * @param {Employee[]} employees - The shop's employees (for technician /
 * service advisor name lookup).
 * @param {ToastFieldKey} key - The field to resolve.
 * @returns {string} The resolved value, or "" when unavailable.
 */
function resolveOne(
  card: Card,
  shop: Shop,
  employees: Employee[],
  key: ToastFieldKey
): string {
  if (key === "Promise Time") {
    const at = firestoreToDateTime(card.promisedTime);
    return at
      ? at.toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        })
      : "";
  }
  if (key === "Container") {
    if (!card.currentContainer) return "";
    const name = getContainerName(card.currentContainer, shop.containers);
    return name === UNKNOWN_CONTAINER_SENTINEL ? "" : name;
  }
  if (key === "Technician") {
    const name = getEmployeeName(card.technicianId, employees);
    return name === UNKNOWN_EMPLOYEE_SENTINEL ? "" : name;
  }
  if (key === "Service Advisor") {
    const name = getEmployeeName(card.serviceWriterId, employees);
    return name === UNKNOWN_EMPLOYEE_SENTINEL ? "" : name;
  }

  // Everything else is a card-layout field: borrow the board's own
  // resolution via its explicit field-override parameter, against the real,
  // unmodified shop. This keeps one source of truth with the card face and
  // avoids the manual-card hand-typed-slot-text bug a shop-clone override
  // would hit (see getCardSection's fieldOverride doc).
  return getCardSection(card, shop, "primaryField", key) ?? "";
}

/**
 * Resolves the configured field slots, dropping empty ones.
 * @param {Card} card - The card.
 * @param {Shop} shop - The shop.
 * @param {Employee[]} employees - The shop's employees (for technician /
 * service advisor name lookup).
 * @param {(ToastFieldKey | null)[]} keys - The configured slots.
 * @returns {string[]} Non-empty resolved values, in slot order.
 */
export function resolveToastFields(
  card: Card,
  shop: Shop,
  employees: Employee[],
  keys: (ToastFieldKey | null)[]
): string[] {
  return keys
    .map((key) => (key ? resolveOne(card, shop, employees, key) : ""))
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter((v) => v.length > 0);
}
